<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="optionsData"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
  >
    <div class="simple-data-with-variation-container">
      <div class="simple-data-with-variation-container__current-data">
        <div
          v-tooltip="tooltip"
          class="simple-data-with-variation-container__value"
        >
          {{ prefix }} {{ labelValue }}
        </div>
        <div class="simple-data-with-variation-container__variation">
          <StIcon
            :color="variationIconColor"
            :icon-name="variationIcon"
          />
          <div :style="{ color: variationIconColor }">
            {{ labelVariation }}
          </div>
        </div>
      </div>

      <div
        v-if="showComparation"
        class="simple-data-with-variation-container__comparation-data"
      >
        {{ $t('components.models.SimpleDataWithVariation.label.description') }}
        <div class="simple-data-with-variation-container__comparation-data--bold">
          {{ previousLabelValue }}
        </div>
        {{ previousDataLabel }}
      </div>
    </div>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import colors from '@/utils/colors';
import { formatSocialNumberWithDecimal, formatNumber } from '@/utils/formatter';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import { mapState } from 'vuex';
import Badge from '@/components/Badge';

export default {
  name: 'SimpleDataWithVariation',
  components: {
    StIcon,
    CardWithTitle,
    Badge,
  },
  mixins: [
    cardsModel,
    exportModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    variation: {
      type: [String, Number],
      default: null,
    },
    rawVariation: {
      type: [String, Number],
      default: '0',
    },
    comparationValue: {
      type: [String, Number],
      default: undefined,
    },
    moreIsBetter: {
      type: Boolean,
      default: true,
    },
    prefix: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('misc', [
      'previousDataLabel',
    ]),
    isPositive() {
      return this.rawVariation > 0;
    },
    isNegative() {
      return this.rawVariation < 0;
    },
    variationIcon() {
      if (this.isPositive) {
        return 'arrow-up';
      }

      if (this.isNegative) {
        return 'arrow-down';
      }

      return 'minus';
    },
    variationIconColor() {
      const { positiveColor, negativeColor, neutralColor } = colors;

      if (this.card.dataType === DATA_TYPE.BLIP) {
        return 'var(--ds-color-text-2)';
      }

      if (this.isPositive) {
        return this.moreIsBetter ? positiveColor : negativeColor;
      }

      if (this.isNegative) {
        return this.moreIsBetter ? negativeColor : positiveColor;
      }

      return neutralColor;
    },
    labelValue() {
      return this.formatValue(this.value);
    },
    previousLabelValue() {
      return this.formatValue(this.comparationValue);
    },
    tooltip() {
      if (Number.isNaN(Number(this.value)) || this.value < 1000) {
        return null;
      }

      return `${formatNumber(this.value)}`;
    },
    labelVariation() {
      const isThereVariation = this.variation !== null;

      return isThereVariation ? `${formatNumber(this.variation)}%` : '-';
    },
    showComparation() {
      return this.comparationValue !== undefined;
    },
    optionsData() {
      return this.baseCardOptions.concat(this.exportOptionsData).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
    },
  },
  methods: {
    formatValue(value) {
      if (Number.isNaN(Number(value))) {
        return value;
      }

      return `${formatSocialNumberWithDecimal(value)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
  .simple-data-with-variation-container {
    height: 90%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;

    &__current-data,
    &__comparation-data {
      display: flex;
      align-items: flex-end;
    }

    &__value {
      display: flex;
      font-size: var(--ds-font-size-xl);
      font-weight: var(--ds-font-weight-semibold);
      white-space: nowrap;
    }

    &__variation {
      display: flex;
      line-height: 34px;
      font-size: var(--ds-font-size-sm);
      font-weight: var(--ds-font-weight-semibold);
      white-space: nowrap;

      i {
        font-size: var(--ds-font-size-baseline);
        margin-left: var(--ds-spacing-inline-quark);
      }
    }

    &__comparation-data {
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-xs);
      line-height: 16px;

      &--bold {
        font-weight: var(--ds-font-weight-bold);
        display: contents;
      }
    }
  }
</style>
